/**
 * Notification Center - Full-page notification management
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';
import { 
  listNotifications, 
  markNotificationAsRead, 
  deleteNotification, 
  markAllNotificationsAsRead,
  Notification,
  NotificationListParams,
} from '../../api/notifications';
import { useNotificationWebSocket } from '../../hooks/useNotificationWebSocket';
import { useAuthContext } from '../Auth/AuthProvider';
import { X, Check, Trash2, CheckCheck, Filter, ExternalLink, Settings } from 'lucide-react';
import { Button, Card } from '../UI';
import { NotificationPreferences } from './NotificationPreferences';
import { useWorkspace } from '../../hooks/useWorkspace';

export interface NotificationCenterProps {
  onClose?: () => void;
}

export function NotificationCenter({ onClose }: NotificationCenterProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const { projectId } = useWorkspace();
  const [filters, setFilters] = useState<NotificationListParams>({
    page: 1,
    page_size: 50,
  });
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  const [showPreferences, setShowPreferences] = useState(false);

  // Enable WebSocket for real-time updates
  useNotificationWebSocket();

  // Fetch notifications (backend will look up user by email for master key users)
  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ['notifications', 'list', filters, user?.id, user?.email],
    queryFn: async () => {
      if (import.meta.env.DEV) {
        console.log('[NotificationCenter] Fetching notifications with params:', { ...filters, user_id: user?.id, user_email: user?.email });
      }
      const result = await listNotifications({ ...filters, user_id: user?.id });
      if (import.meta.env.DEV) {
        console.log('[NotificationCenter] listNotifications result:', {
          notifications_count: result?.notifications?.length || 0,
          total: result?.total || 0,
          unread_count: result?.unread_count || 0,
          full_result: result,
        });
      }
      return result;
    },
    enabled: !!user?.id || !!user?.email,
    retry: 1,
  });

  // Mutations
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => markNotificationAsRead(notificationId, user?.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (notificationId: string) => deleteNotification(notificationId, user?.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setSelectedNotifications(new Set());
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => markAllNotificationsAsRead(user?.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Group notifications by date
  const groupedNotifications = useMemo(() => {
    if (!data?.notifications) return {};

    const groups: Record<string, Notification[]> = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'Older': [],
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    data.notifications.forEach((notification) => {
      const date = new Date(notification.created_at);
      
      if (date >= today) {
        groups['Today'].push(notification);
      } else if (date >= yesterday) {
        groups['Yesterday'].push(notification);
      } else if (date >= weekAgo) {
        groups['This Week'].push(notification);
      } else {
        groups['Older'].push(notification);
      }
    });

    // Remove empty groups
    Object.keys(groups).forEach((key) => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  }, [data?.notifications]);

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsReadMutation.mutateAsync(notificationId);
  };

  const handleDelete = async (notificationId: string) => {
    if (confirm('Delete this notification?')) {
      await deleteMutation.mutateAsync(notificationId);
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsReadMutation.mutateAsync();
  };

  const handleBulkDelete = async () => {
    if (selectedNotifications.size === 0) return;
    if (confirm(`Delete ${selectedNotifications.size} selected notifications?`)) {
      await Promise.all(
        Array.from(selectedNotifications).map((id) => deleteMutation.mutateAsync(id))
      );
    }
  };

  const toggleSelection = (notificationId: string) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(notificationId)) {
      newSelected.delete(notificationId);
    } else {
      newSelected.add(notificationId);
    }
    setSelectedNotifications(newSelected);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'border-red-500 bg-red-50 dark:bg-red-900/20';
      case 'warning':
        return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
      default:
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20';
    }
  };

  // Show preferences view if toggled
  if (showPreferences) {
    return (
      <NotificationPreferences 
        projectId={projectId}
        onClose={() => setShowPreferences(false)}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Notifications</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {data?.unread_count || 0} unread • {data?.total || 0} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreferences(true)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Preferences
          </Button>
          {onClose && (
            <Button variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleMarkAllAsRead}
            disabled={markAllAsReadMutation.isPending || (data?.unread_count || 0) === 0}
          >
            <CheckCheck className="h-4 w-4 mr-2" />
            Mark All Read
          </Button>
          {selectedNotifications.size > 0 && (
            <Button
              variant="outline"
              onClick={handleBulkDelete}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Selected ({selectedNotifications.size})
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div className="flex items-center gap-4">
          <select
            value={filters.read === undefined ? 'all' : filters.read ? 'read' : 'unread'}
            onChange={(e) => {
              setFilters({
                ...filters,
                read: e.target.value === 'all' ? undefined : e.target.value === 'read',
                page: 1,
              });
            }}
            className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>

          <select
            value={filters.type || 'all'}
            onChange={(e) => {
              setFilters({
                ...filters,
                type: e.target.value === 'all' ? undefined : e.target.value,
                page: 1,
              });
            }}
            className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option value="all">All Types</option>
            <option value="job_completion">Job Completion</option>
            <option value="report_comment">Report Comment</option>
            <option value="report_status_change">Report Status</option>
            <option value="new_opportunity">New Opportunity</option>
            <option value="keyword_recommendation">Keyword Recommendation</option>
            <option value="industry_news">Industry News</option>
          </select>

          <select
            value={filters.severity || 'all'}
            onChange={(e) => {
              setFilters({
                ...filters,
                severity: e.target.value === 'all' ? undefined : e.target.value,
                page: 1,
              });
            }}
            className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto p-6">
        {error && (
          <div className="text-center py-12 text-red-500 dark:text-red-400">
            <p className="text-lg mb-2">Error loading notifications</p>
            <p className="text-sm">{error instanceof Error ? error.message : String(error)}</p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        )}
        {isLoading ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            Loading notifications...
          </div>
        ) : !error && Object.keys(groupedNotifications).length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            <p className="text-lg mb-2">No notifications</p>
            <p className="text-sm">You're all caught up!</p>
            {import.meta.env.DEV && (
              <div className="mt-4 text-xs text-slate-400">
                <p>Debug: user_id={user?.id}, email={user?.email}</p>
                <p>Debug: data={JSON.stringify(data, null, 2)}</p>
              </div>
            )}
          </div>
        ) : !error ? (
          <div className="space-y-6">
            {Object.entries(groupedNotifications).map(([groupName, groupNotifications]) => (
              <div key={groupName}>
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wide">
                  {groupName}
                </h2>
                <div className="space-y-3">
                  {groupNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`
                        p-4 cursor-pointer transition-all rounded-lg border
                        ${!notification.read ? 'ring-2 ring-blue-500' : ''}
                        ${getSeverityColor(notification.severity)}
                        hover:shadow-md
                      `}
                      onClick={() => {
                        if (!notification.read) {
                          handleMarkAsRead(notification.id);
                        }
                        if (notification.action_url) {
                          window.location.href = notification.action_url;
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedNotifications.has(notification.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelection(notification.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                              {notification.title}
                            </h3>
                            {!notification.read && (
                              <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full" />
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded ${getSeverityColor(notification.severity)}`}>
                              {notification.severity}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                            <span>
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </span>
                            {notification.type && (
                              <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded">
                                {notification.type.replace(/_/g, ' ')}
                              </span>
                            )}
                            {notification.action_url && (
                              <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                                <ExternalLink className="h-3 w-3" />
                                View
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!notification.read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notification.id);
                              }}
                              className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400"
                              title="Mark as read"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(notification.id);
                            }}
                            className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Pagination */}
        {data && data.total > filters.page_size! && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
              disabled={filters.page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Page {filters.page} of {Math.ceil(data.total / filters.page_size!)}
            </span>
            <Button
              variant="outline"
              onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
              disabled={filters.page! >= Math.ceil(data.total / filters.page_size!)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

